// src/views/parent/PAProfil.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useParentService } from "../../services/parentService";
import { useParentDrawer } from "../../context/ParentDrawerContext";

// ─── TOAST ──────────────────────────────────────────────────────────────
function Toast({ message, subtitle, isError, isSuccess, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const icon = isSuccess ? "ti-check" : "ti-x";
  const bgColor = isSuccess ? "var(--gn)" : isError ? "var(--rd)" : "var(--pr)";

  return (
    <div className="toast-container">
      <div className="toast-content" style={{ background: bgColor }}>
        <div className="toast-icon">
          <i className={`ti ${icon}`} />
        </div>
        <div className="toast-text">
          <div className="toast-message">{message}</div>
          {subtitle && <div className="toast-subtitle">{subtitle}</div>}
        </div>
        <button className="toast-close" onClick={onClose}>
          <i className="ti ti-x" />
        </button>
      </div>
    </div>
  );
}

// ─── CHAMP DE SAISIE ──────────────────────────────────────────────────
function InputField({
  label, value, onChange, icon, type = "text",
  isPassword = false, readOnly = false, hint = null,
}) {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className="input-field">
      <div className={`input-wrapper ${isFocused ? "focused" : ""} ${readOnly ? "readonly" : ""}`}>
        <div className="input-icon">
          <i className={`ti ${icon}`} />
        </div>
        <input
          type={isPassword ? "password" : type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={label}
          className="input-control"
          readOnly={readOnly}
          disabled={readOnly}
        />
        {readOnly && (
          <div className="input-lock" title="Non modifiable">
            <i className="ti ti-lock" />
          </div>
        )}
      </div>
      <div className="input-label">
        {label}
        {hint && <span className="input-hint"> · {hint}</span>}
      </div>
    </div>
  );
}

// ─── SKELETON LOADER ──────────────────────────────────────────────────
function SkeletonLoader() {
  return (
    <div style={{ background: "var(--s2)", minHeight: "100dvh" }}>
      <div className="skeleton-header" />
      <div className="skeleton-content">
        <div className="skeleton-title" />
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="skeleton-field" style={{ animationDelay: `${i * 0.1}s` }} />
        ))}
        <div className="skeleton-title" style={{ marginTop: 24 }} />
        {[1, 2, 3].map(i => (
          <div key={i} className="skeleton-field" style={{ animationDelay: `${(i + 4) * 0.1}s` }} />
        ))}
        <div className="skeleton-btn" />
      </div>
    </div>
  );
}

// ─── HEADER ──────────────────────────────────────────────────────────
function ProfileHeader({ slug }) {
  const navigate = useNavigate();
  const { openDrawer } = useParentDrawer();

  return (
    <div className="profile-header">
      <button className="menu-btn" onClick={openDrawer} aria-label="Ouvrir le menu">
        <i className="ti ti-menu-2" />
      </button>

      <button
        className="back-home-btn"
        onClick={() => navigate(`/${slug}/parent/accueil`)}
        aria-label="Retour à l'accueil"
      >
        <i className="ti ti-home" />
      </button>

      <div className="profile-avatar">
        <i className="ti ti-user" />
      </div>
      <h1>Mon profil</h1>
      <p>Gérez vos informations personnelles</p>
    </div>
  );
}

// ─── STYLES ──────────────────────────────────────────────────────────
function ProfileStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');

      *, *::before, *::after { box-sizing: border-box; }

      @keyframes skPulse {
        from { opacity: 0.55; }
        to { opacity: 1; }
      }
      .skeleton-header {
        height: 210px;
        background: linear-gradient(135deg, var(--pr-bg), var(--pr-lt));
        border-radius: 0 0 40px 40px;
        animation: skPulse 1.2s ease-in-out infinite alternate;
      }
      .skeleton-content { padding: 24px; }
      .skeleton-title {
        height: 24px; width: 180px; background: var(--b2);
        border-radius: 8px; margin-bottom: 16px;
        animation: skPulse 1.2s ease-in-out infinite alternate;
      }
      .skeleton-field {
        height: 56px; background: var(--b2);
        border-radius: 12px; margin-bottom: 12px;
        animation: skPulse 1.2s ease-in-out infinite alternate;
      }
      .skeleton-btn {
        height: 52px; width: 160px; background: var(--b2);
        border-radius: 30px; margin: 16px auto 0;
        animation: skPulse 1.2s ease-in-out infinite alternate;
      }

      .profile-page {
        min-height: 100vh;
        background: var(--s2);
        padding-bottom: 24px;
      }

      .profile-header {
        background: linear-gradient(135deg, var(--pr-dk), var(--pr));
        padding: 52px 20px 40px;
        text-align: center;
        border-radius: 0 0 40px 40px;
        box-shadow: 0 4px 15px rgba(0,0,0,0.15);
        position: relative;
        overflow: hidden;
      }
      .profile-header::after {
        content: "";
        position: absolute;
        left: 20%; right: 20%; bottom: 0;
        height: 3px;
        background: linear-gradient(90deg, transparent, var(--or), transparent);
      }
      .profile-header .menu-btn {
        position: absolute; top: 44px; left: 16px;
        background: rgba(255,255,255,0.2);
        border: none; border-radius: 12px;
        padding: 8px 10px; cursor: pointer; color: #fff;
        font-size: 20px; width: 42px; height: 42px;
        display: flex; align-items: center; justify-content: center;
        z-index: 10; transition: background 0.15s;
      }
      .profile-header .menu-btn:hover { background: rgba(255,255,255,0.3); }

      .profile-header .back-home-btn {
        position: absolute; top: 44px; right: 16px;
        background: rgba(255,255,255,0.2);
        border: none; border-radius: 12px;
        padding: 8px 10px; cursor: pointer; color: #fff;
        font-size: 20px; width: 42px; height: 42px;
        display: flex; align-items: center; justify-content: center;
        z-index: 10; transition: background 0.15s;
      }
      .profile-header .back-home-btn:hover { background: rgba(255,255,255,0.3); }

      .profile-header .profile-avatar {
        width: 64px; height: 64px; border-radius: 50%;
        background: rgba(255,255,255,0.2);
        display: inline-flex; align-items: center; justify-content: center;
        margin-bottom: 12px;
      }
      .profile-header .profile-avatar i { font-size: 32px; color: #fff; }

      .profile-header h1 {
        font-family: 'Poppins', sans-serif;
        color: #fff; font-size: 22px; font-weight: 700; margin: 0;
      }
      .profile-header p {
        font-family: 'Inter', sans-serif;
        color: rgba(255,255,255,0.75);
        font-size: 14px; margin: 8px 0 0;
      }

      .profile-content { padding: 20px 16px; }

      .profile-section-title {
        font-family: 'Poppins', sans-serif;
        font-size: 17px; font-weight: 700; color: var(--pr);
        margin: 0 0 14px; display: flex; align-items: center;
      }
      .profile-section-title .badge {
        background: var(--pr-bg); padding: 2px 10px;
        border-radius: 20px; font-size: 11px; font-weight: 500;
        color: var(--pr); margin-left: 8px;
      }

      .profile-section { margin-bottom: 28px; }
      .input-field { margin-bottom: 14px; }

      .input-wrapper {
        display: flex; align-items: center;
        border: 2px solid var(--b); border-radius: 14px;
        transition: all 0.2s ease; overflow: hidden; background: #fff;
      }
      .input-wrapper.focused {
        border-color: var(--pr);
        box-shadow: 0 0 0 0.25rem var(--pr-bg);
      }
      .input-wrapper.readonly {
        background: var(--s2);
        border-color: var(--b);
        opacity: 0.85;
      }
      .input-wrapper .input-icon {
        padding: 0 14px; color: var(--pr);
        display: flex; align-items: center;
      }
      .input-wrapper .input-icon i { font-size: 18px; }
      .input-wrapper .input-control {
        flex: 1; padding: 14px 14px 14px 0;
        border: none; outline: none;
        font-family: 'Inter', sans-serif; font-size: 15px;
        background: transparent; color: var(--t);
      }
      .input-wrapper .input-control::placeholder { color: var(--h); }
      .input-wrapper.readonly .input-control {
        cursor: not-allowed; color: var(--m);
      }
      .input-wrapper .input-lock {
        padding: 0 14px; color: var(--h); font-size: 14px;
      }

      .input-label {
        position: relative; top: -2px; left: 16px;
        background: #fff; padding: 0 4px;
        font-size: 11px; color: var(--m);
        font-family: 'Inter', sans-serif; width: fit-content;
      }
      .input-hint { color: var(--h); font-style: italic; }
      .input-wrapper.focused ~ .input-label { color: var(--pr); }

      .btn-save {
        display: flex; align-items: center; justify-content: center;
        gap: 10px; background: var(--pr); color: #fff;
        border: none; border-radius: 30px;
        padding: 14px 28px;
        font-family: 'Inter', sans-serif;
        font-size: 16px; font-weight: 600;
        cursor: pointer; transition: all 0.2s ease;
        margin: 0 auto; min-width: 200px;
      }
      .btn-save:hover:not(:disabled) {
        background: var(--pr-dk);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      }
      .btn-save:disabled {
        opacity: 0.7; cursor: default;
        min-width: 56px; padding: 14px;
      }
      .btn-save .spinner {
        width: 22px; height: 22px;
        border: 2px solid #fff;
        border-top-color: transparent;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }
      @keyframes spin { to { transform: rotate(360deg); } }

      .toast-container {
        position: fixed; top: 80px; left: 16px; right: 16px;
        z-index: 2000; animation: slideDown 0.3s ease-out;
        max-width: 430px; margin: 0 auto;
      }
      .toast-content {
        padding: 14px 18px; border-radius: 16px;
        box-shadow: 0 8px 20px rgba(0,0,0,0.15);
        display: flex; align-items: center; gap: 14px;
      }
      .toast-icon {
        width: 36px; height: 36px; border-radius: 50%;
        background: rgba(255,255,255,0.2);
        display: flex; align-items: center; justify-content: center;
      }
      .toast-icon i { color: #fff; font-size: 18px; }
      .toast-text { flex: 1; }
      .toast-message {
        font-family: 'Inter', sans-serif;
        color: #fff; font-weight: 700; font-size: 14px;
      }
      .toast-subtitle {
        font-family: 'Inter', sans-serif;
        color: rgba(255,255,255,0.85);
        font-size: 12px; margin-top: 2px;
      }
      .toast-close {
        width: 28px; height: 28px; border-radius: 50%;
        background: rgba(255,255,255,0.2); border: none;
        cursor: pointer; display: flex;
        align-items: center; justify-content: center;
      }
      .toast-close i { color: rgba(255,255,255,0.8); font-size: 16px; }

      @keyframes slideDown {
        from { opacity: 0; transform: translateY(-20px); }
        to { opacity: 1; transform: translateY(0); }
      }

      /* ── État erreur ── */
      .profile-error-card {
        background: #fff;
        border-radius: 20px;
        padding: 40px 20px;
        text-align: center;
        box-shadow: 0 4px 18px rgba(0,0,0,0.08);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        margin-top: 8px;
      }
      .profile-error-card i.alert-icon {
        font-size: 48px;
        color: var(--rd);
      }
      .profile-error-card h2 {
        font-family: 'Poppins', sans-serif;
        font-size: 17px; font-weight: 600;
        color: var(--t); margin: 4px 0 0;
      }
      .profile-error-card p {
        font-family: 'Inter', sans-serif;
        font-size: 13px; color: var(--m);
        margin: 0 0 12px;
      }
      .profile-error-card .actions-row {
        display: flex; gap: 8px;
        flex-wrap: wrap; justify-content: center;
      }
      .profile-error-card .btn {
        border: none; border-radius: 30px;
        padding: 10px 22px; cursor: pointer;
        font-family: 'Inter', sans-serif;
        font-size: 13px; font-weight: 600;
        color: #fff; background: var(--pr);
        display: inline-flex; align-items: center; gap: 6px;
        transition: opacity 0.2s;
      }
      .profile-error-card .btn:hover { opacity: 0.9; }
      .profile-error-card .btn.btn-secondary {
        background: var(--b2); color: var(--t);
      }

      @media (max-width: 480px) {
        .profile-header { padding: 44px 16px 32px; }
        .profile-header h1 { font-size: 19px; }
        .profile-content { padding: 16px 12px; }
        .btn-save { min-width: 100%; }
      }
    `}</style>
  );
}

// ─── PAGE PRINCIPALE ──────────────────────────────────────────────────
export default function PAProfil({ slug }) {
  const navigate = useNavigate();
  const { getProfil, updateProfil, changerMotDePasse } = useParentService();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [username, setUsername] = useState("");
  const [telephone1, setTelephone1] = useState("");
  const [telephone2, setTelephone2] = useState("");
  const [email, setEmail] = useState("");
  const [first_name, setFirstName] = useState("");
  const [last_name, setLastName] = useState("");

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const hasFetchedRef = useRef(false);

  const showToast = useCallback(({ message, subtitle, isError, isSuccess }) => {
    setToast({ message, subtitle, isError, isSuccess });
  }, []);

  // ─── CHARGEMENT DU PROFIL ─────────────────────────────────────────
  const loadProfil = useCallback(async () => {
    if (!slug) {
      setError("Identifiant parent manquant.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getProfil(slug);
      setUsername(data?.username || "");
      setFirstName(data?.first_name || "");
      setLastName(data?.last_name || "");
      setTelephone1(data?.telephone1 || "");
      setTelephone2(data?.telephone2 || "");
      setEmail(data?.email || "");
    } catch (err) {
      setError("Impossible de récupérer les informations du profil.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    loadProfil();
  }, [loadProfil]);

  // ─── SAUVEGARDE ────────────────────────────────────────────────────
  const handleSave = async () => {
    const isPasswordChange = oldPassword || newPassword || confirmPassword;

    if (isPasswordChange) {
      if (!oldPassword || !newPassword || !confirmPassword) {
        showToast({
          message: "Champs incomplets",
          subtitle: "Remplissez tous les champs mot de passe",
          isError: true,
        });
        return;
      }
      if (newPassword !== confirmPassword) {
        showToast({
          message: "Erreur de confirmation",
          subtitle: "Les mots de passe ne correspondent pas",
          isError: true,
        });
        return;
      }
      if (newPassword.length < 6) {
        showToast({
          message: "Mot de passe trop court",
          subtitle: "Le mot de passe doit contenir au moins 6 caractères",
          isError: true,
        });
        return;
      }
    }

    const profileBody = {
      first_name,
      last_name,
      telephone1,
      telephone2,
      email,
    };

    setSaving(true);

    try {
      await updateProfil(slug, profileBody);

      if (isPasswordChange) {
        await changerMotDePasse(slug, {
          ancien_mot_de_passe: oldPassword,
          nouveau_mot_de_passe: newPassword,
        });
      }

      showToast({
        message: "Profil mis à jour avec succès !",
        subtitle: isPasswordChange
          ? "Vos informations et votre mot de passe ont été modifiés"
          : "Vos modifications ont été enregistrées",
        isSuccess: true,
      });

      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      const data = err.response?.data;

      if (data?.error) {
        const msg = Array.isArray(data.error) ? data.error[0] : data.error;
        showToast({ message: "Erreur mot de passe", subtitle: msg, isError: true });
      } else if (data && typeof data === "object") {
        const firstKey = Object.keys(data)[0];
        const firstError = data[firstKey];
        const msg = Array.isArray(firstError) ? firstError[0] : String(firstError);
        const label = {
          first_name: "Prénom",
          last_name: "Nom",
          email: "Email",
          telephone1: "Téléphone 1",
          telephone2: "Téléphone 2",
        }[firstKey] || firstKey;
        showToast({ message: `Erreur · ${label}`, subtitle: msg, isError: true });
      } else {
        showToast({
          message: "Erreur",
          subtitle: "Vérifiez vos informations et réessayez",
          isError: true,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  // ─── RENDU ─────────────────────────────────────────────────────────
  // Écran de chargement : contient sa propre structure, pas de page blanche
  if (loading) {
    return (
      <>
        <ProfileStyles />
        <SkeletonLoader />
      </>
    );
  }

  return (
    <>
      <ProfileStyles />

      <div className="profile-page">
        {toast && (
          <Toast
            message={toast.message}
            subtitle={toast.subtitle}
            isError={toast.isError}
            isSuccess={toast.isSuccess}
            onClose={() => setToast(null)}
          />
        )}

        {/* Header TOUJOURS affiché (menu + retour accessibles) */}
        <ProfileHeader slug={slug} />

        <div className="profile-content">
          {error ? (
            /* ── ÉTAT ERREUR (header conservé au-dessus) ── */
            <div className="profile-error-card">
              <i className="ti ti-alert-circle alert-icon" />
              <h2>Erreur de chargement</h2>
              <p>{error}</p>
              <div className="actions-row">
                <button className="btn" onClick={loadProfil}>
                  <i className="ti ti-refresh" />
                  Réessayer
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate(`/${slug}/parent/accueil`)}
                >
                  <i className="ti ti-home" />
                  Accueil
                </button>
              </div>
            </div>
          ) : (
            /* ── FORMULAIRE ── */
            <>
              <div className="profile-section">
                <h2 className="profile-section-title">
                  <i className="ti ti-user" style={{ marginRight: 8 }} />
                  Informations personnelles
                </h2>

                <InputField label="Prénom" value={first_name} onChange={setFirstName} icon="ti-user" />
                <InputField label="Nom" value={last_name} onChange={setLastName} icon="ti-user" />
                <InputField
                  label="Nom d'utilisateur"
                  value={username}
                  onChange={() => {}}
                  icon="ti-user-check"
                  readOnly
                  hint="non modifiable"
                />
                <InputField label="Email" value={email} onChange={setEmail} icon="ti-mail" type="email" />
                <InputField label="Téléphone 1" value={telephone1} onChange={setTelephone1} icon="ti-phone" />
                <InputField label="Téléphone 2" value={telephone2} onChange={setTelephone2} icon="ti-phone" />
              </div>

              <div className="profile-section">
                <h2 className="profile-section-title">
                  <i className="ti ti-lock" style={{ marginRight: 8 }} />
                  Changer le mot de passe
                  <span className="badge">Optionnel</span>
                </h2>

                <InputField
                  label="Mot de passe actuel"
                  value={oldPassword}
                  onChange={setOldPassword}
                  icon="ti-lock"
                  isPassword
                />
                <InputField
                  label="Nouveau mot de passe"
                  value={newPassword}
                  onChange={setNewPassword}
                  icon="ti-lock"
                  isPassword
                />
                <InputField
                  label="Confirmer le mot de passe"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  icon="ti-lock"
                  isPassword
                />
              </div>

              <button className="btn-save" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <div className="spinner" />
                ) : (
                  <>
                    <i className="ti ti-device-floppy" style={{ fontSize: 18 }} />
                    Sauvegarder
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}